import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { array, boolean, number, object, ObjectSchema, string } from 'yup'
import { CreateCandidateNameRequest } from '../../../models/chart'
import {
  AdminChartBaseFormValues,
  adminChartBaseValidationSchema,
} from './admin-base'

export type AdminPresidentialChartProposalsFormValues = {
  name: string
}

export type AdminPresidentChartRacePartiesFormValues = {
  index?: number
  abbv: string
  name: string
  order: number
  candidates: number
  candidateNames: CreateCandidateNameRequest[]
}

export type AdminPresidentialChartPartiesFormValues = {
  id?: number
  abbv: string
  name: string
  selected: boolean
  candidates: number
  writeIns: number
  voteFor: number
  politicalParties: AdminPresidentChartRacePartiesFormValues[]
  raceType: string
}

export type AdminPresidentialChartRaceFormValues = {
  id?: string
  name: string
  type: string
  raceType: string
  candidates: number
  writeIns: number
  voteFor: number
  party: string
  politicalParties: AdminPresidentChartRacePartiesFormValues[]
}

export interface AdminPresidentialChartFormValues
  extends AdminChartBaseFormValues {
  proposals: AdminPresidentialChartProposalsFormValues[]
  parties: AdminPresidentialChartPartiesFormValues[]
  races: AdminPresidentialChartRaceFormValues[]
  proposalOnly: boolean
}

const chartValidationSchema: ObjectSchema<AdminPresidentialChartFormValues> =
  object({
    ...adminChartBaseValidationSchema,
    proposals: array()
      .of(
        object({
          name: string().required(),
        })
      )
      .required()
      .default([]),
    parties: array()
      .of(
        object({
          id: number(),
          abbv: string().required(),
          name: string().required(),
          raceType: string().default('Partisan').required(),
          selected: boolean().required(),
          candidates: number().default(0).required(),
          writeIns: number().default(1).required(),
          voteFor: number().default(1).required(),
          politicalParties: array()
            .of(
              object({
                index: number().optional(),
                abbv: string().required(),
                name: string().required(),
                order: number().default(0).required(),
                candidates: number().default(0).required(),
                candidateNames: array()
                  .of(
                    object({
                      name: string().required(),
                      order: number().required(),
                    })
                  )
                  .required()
                  .default([]),
              })
            )
            .default([])
            .required(),
        })
      )
      .required()
      .test({
        message: 'Chart must have at least one party',
        test: (arr) => (arr ? arr.filter((p) => p.selected).length > 0 : false),
      }),
    races: array()
      .when('proposalOnly', {
        is: false,
        then: (schema) =>
          schema.of(
            object({
              id: string(),
              name: string().required('Name is required'),
              type: string().default('Race').required(),
              raceType: string().required(),
              candidates: number().default(0).required(),
              writeIns: number().required(),
              voteFor: number().required(),
              politicalParties: array()
                .of(
                  object({
                    name: string().required('You must select a party for race'),
                    abbv: string(),
                    candidates: number().default(0).required(),
                  })
                )
                .default([]),
              candidateNames: array().of(
                object({
                  name: string().required(),
                  order: number().required(),
                })
              ),
            }).required()
          ),
        otherwise: (schema) => schema,
      })
      .required(),
    proposalOnly: boolean().required().default(false),
  }).required()

export function useAdminPresidentialChartForm() {
  return useForm<AdminPresidentialChartFormValues>({
    resolver: yupResolver(chartValidationSchema),
    // comment out above resolver if form isn't submitting and uncomment this block to debug
    /*resolver: async (data, context, options) => {
      // you can debug your validation schema here
      console.log('formData', data)
      console.log(
        'validation result',
        await yupResolver(chartValidationSchema)(data, context, options)
      )
      return yupResolver(chartValidationSchema)(data, context, options)
    },*/
    reValidateMode: 'onChange',
    defaultValues: { proposals: [] },
  })
}
